'use client';
/* eslint @typescript-eslint/no-explicit-any: 0 */ // --> OFF

import { createContext, useContextSelector } from 'use-context-selector';
import useCookie, { cookieOptions } from 'react-use-cookie';
import {
  TelegramCurrencyResponseCurrencyEnum,
  telegramGeoGetCountries,
  useTelegramGeoDetectGetLocation,
  useTelegramUsersGetMe,
  useTelegramUsersPatch,
} from '@/generated';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createCorrectGeoPathFromData } from '@/utils/SEOPathModule/utils';
import { useRouter } from '@/hooks/useRouter';
import { usePathname } from '@/navigation';
import { useLocale } from 'next-intl';
import { runtimeConfig } from '@/config/runtime-config';
import { useModals } from './ModalContext';
import { ChangeWLModal } from '@/modals/ChangeWLModal';
import { routing } from '@/i18n/routing';
import { isAppLocale } from '@/i18n/locales';

type changeCookie = (val: string, opt: cookieOptions) => void;

type Coords = {
  lat: number;
  lon: number;
};

type UpdateLocation = (
  countryId: string,
  regionId: string,
  cityId: string,
  coords: Coords,
  cbIfNotModal?: (_?: any) => void,
) => void;

type CookieStorageContext = {
  regionId: string;
  cityId: string;
  countryId: string;
  coords?: Coords;
  districtId?: string;
  currency?: TelegramCurrencyResponseCurrencyEnum;
  wlMode?: boolean;
  geoSearchString?: string;
  setCurrency: (newCurrency: TelegramCurrencyResponseCurrencyEnum) => void;
  updateLocation: UpdateLocation;
  updateLocationWithoutCheck: UpdateLocation;
};

const defaultNoop = (_?: string) => {};

export const CookieStorageContext = createContext<CookieStorageContext>({
  regionId: '',
  cityId: '',
  countryId: '',
  updateLocation: defaultNoop,
  setCurrency: defaultNoop,
  updateLocationWithoutCheck: defaultNoop,
});

const setter = (func: changeCookie) => (arg: string) => {
  func(arg, {
    days: 365,
    SameSite: 'Lax',
    Secure: false,
  });
};

const modalKey = 'wl-routing-modal';

export type InitialCookieValues = {
  countryId?: string;
  regionId?: string;
  cityId?: string;
  currency?: string;
};

export const CookieStorageContextProvider = ({
  children,
  initialValues = {},
}: {
  children: React.ReactNode;
  initialValues?: InitialCookieValues;
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const isUnsubscribePage = pathname === '/unsubscribe';

  const [regionId, _setRegionId] = useCookie('doska-geo-region', initialValues.regionId ?? '');
  const [cityId, _setCityId] = useCookie('doska-geo-city', initialValues.cityId ?? '');
  const [countryId, _setCountryId] = useCookie('doska-geo-country', initialValues.countryId ?? '');
  const [currency, setCurrency] = useCookie('doska-currency', initialValues.currency ?? '');
  const wlMode = !runtimeConfig.isMultiCountry;
  const isGeoExplicitlySetUp = useRef<boolean>(false);

  const { open: openModal, close: closeModal } = useModals();

  const setModalOpen = useCallback(
    (
      state: boolean,
      countryName: string,
      countryLink: string,
      countryCode: string,
      mode?: 'detected' | 'selected',
      hasWhiteLabel?: boolean,
      onGo?: () => void,
    ) => {
      if (state) {
        openModal(
          modalKey,
          <ChangeWLModal
            countryLink={countryLink}
            countryName={countryName}
            countryCode={countryCode}
            hasWhiteLabel={hasWhiteLabel || false}
            mode={mode || 'detected'}
            onGo={onGo}
          />,
          true,
          'md:h-unset h-unset absolute md:static bottom-0 rounded-b-none md:rounded-b-[24px] m-0',
          'bg-black/30',
          true,
          true,
        );
        return;
      }
      closeModal(modalKey);
    },
    [openModal, closeModal],
  );

  const [geoSearchStr, setGeoSearchStr] = useCookie('doska-geo-search-path', '');

  const [coords, _setCoords] = useCookie('doska-tv-geo-coords', '{}');

  const { mutateAsync: updateUser } = useTelegramUsersPatch({
    client: {
      handle401: false,
    },
  });
  const setRegionId = useMemo(() => {
    return setter(_setRegionId);
  }, [_setRegionId]);
  const setCountryId = useMemo(() => {
    return setter(_setCountryId);
  }, [_setCountryId]);
  const setCityId = useMemo(() => {
    return setter(_setCityId);
  }, [_setCityId]);
  const setCoords = useMemo(() => {
    return setter(_setCoords);
  }, [_setCoords]);

  const firedInitialSetSearchString = useRef(false);

  useEffect(() => {
    if (firedInitialSetSearchString.current === false) {
      createCorrectGeoPathFromData({
        countryId,
        regionId,
        cityId,
        isWLMode: wlMode,
        updateGeo: setGeoSearchStr,
      });
      firedInitialSetSearchString.current = true;
    }
  }, [setGeoSearchStr, countryId, regionId, cityId, wlMode, geoSearchStr]);

  const updateFullLocation: UpdateLocation = useCallback(
    async (countryId, regionId, cityId, coords) => {
      isGeoExplicitlySetUp.current = true;
      setCountryId(countryId ?? '');
      setRegionId(regionId ?? '');
      setCityId(cityId ?? '');
      setCoords(JSON.stringify(coords));

      createCorrectGeoPathFromData({
        countryId,
        regionId,
        cityId,
        isWLMode: wlMode,
        updateGeo: setGeoSearchStr,
      });

      const data = await updateUser({
        data: {
          countryId: countryId || null,
          regionId: regionId || null,
          cityId: cityId || null,
        } as any,
      });
      if (data.currency !== null && data.currency !== undefined) {
        return;
      }
      const countries = await telegramGeoGetCountries();
      if (countries) {
        const newCountry = countries.find((country) => country.id === countryId);
        if (newCountry) {
          setCurrency(newCountry.currency);
        }
      }
    },
    [updateUser, setCountryId, setRegionId, setCityId, setCoords, setCurrency, setGeoSearchStr, wlMode],
  );

  const updateFullLocationWithCheckingWL = useCallback(
    async (countryId, regionId, cityId, coords, cbIfNotModal?: (_?: any) => void) => {
      if (runtimeConfig.isMultiCountry && countryId && !runtimeConfig.isTelegramMiniApp) {
        const foundWLCountry = (runtimeConfig.countriesWithWhiteLabels as any).find((val) => val.id === countryId);

        if (foundWLCountry) {
          setModalOpen(
            true,
            foundWLCountry?.whiteLabel.defaultCountry.name[locale] || '',
            foundWLCountry.whiteLabel.domain,
            foundWLCountry.whiteLabel.defaultCountry.countryCode,
            'selected',
            true,
          );
          return;
        }
      }

      updateFullLocation(countryId, regionId, cityId, coords);
      cbIfNotModal?.();
    },
    [setModalOpen, updateFullLocation, locale],
  );

  const { data: IPLocationResponse } = useTelegramGeoDetectGetLocation({
    query: {
      staleTime: Infinity,
      enabled: !isUnsubscribePage,
    },
  });

  const { data: me, error: meError } = useTelegramUsersGetMe({
    query: {
      queryKey: ['get-me-once-unique'],
      staleTime: Infinity,
    },
    client: {
      handle401: false,
    },
  });

  useEffect(() => {
    if (isUnsubscribePage) {
      return;
    }
    if (isGeoExplicitlySetUp.current) {
      return;
    }
    if (me && me?.id && me.geo && me.geo.country?.id && me.geo.region?.id && me.geo.city?.id) {
      const foundWLCountry = (runtimeConfig.countriesWithWhiteLabels as any).find(
        (val) => val.id === me.geo?.country?.id,
      );
      if (runtimeConfig.isMultiCountry && foundWLCountry && !runtimeConfig.isTelegramMiniApp) {
        setModalOpen(
          true,
          me.geo?.country?.name || '',
          foundWLCountry.whiteLabel.domain,
          foundWLCountry.whiteLabel.defaultCountry.countryCode,
          'selected',
          true,
        );
        return;
      }

      setCountryId(me.geo.country?.id);
      setRegionId(me.geo.region?.id);

      setCityId(me.geo.city?.id);

      createCorrectGeoPathFromData({
        countryId: me.geo.country?.id,
        regionId: me.geo.region?.id,
        cityId: me.geo.city?.id,
        isWLMode: wlMode,
        updateGeo: setGeoSearchStr,
      });

      let coordsProvider: { latitude: number; longitude: number } | null = me.geo.country;
      if (me.geo.region?.latitude) {
        coordsProvider = me.geo.region;
      }

      if (me.geo.city?.latitude) {
        coordsProvider = me.geo.city;
      }
      setCoords(
        JSON.stringify({
          lat: coordsProvider?.latitude || 0,
          lon: coordsProvider?.longitude || 0,
        }),
      );

      if (me.currency) {
        setCurrency(me.currency);
      }

      return;
    }

    if (IPLocationResponse) {
      if (!currency) {
        setCurrency(IPLocationResponse.country?.currency || 'ILS');
      }

      if (countryId || regionId || cityId) {
        return;
      }

      const setFinalLocation = () => {
        setCountryId(IPLocationResponse.country?.id || '');
        setRegionId(IPLocationResponse.region?.id || '');
        setCityId(IPLocationResponse.city?.id || '');

        createCorrectGeoPathFromData({
          countryId: IPLocationResponse.country?.id || '',
          regionId: IPLocationResponse.region?.id || '',
          cityId: IPLocationResponse.city?.id || '',
          isWLMode: wlMode,
          updateGeo: setGeoSearchStr,
        });

        let coordsProvider: { latitude: number; longitude: number } | null = IPLocationResponse.country;
        if (IPLocationResponse.region?.latitude) {
          coordsProvider = IPLocationResponse.region;
        }

        if (IPLocationResponse.city?.latitude) {
          coordsProvider = IPLocationResponse.city;
        }
        setCoords(
          JSON.stringify({
            lat: coordsProvider?.latitude || 0,
            lon: coordsProvider?.longitude || 0,
          }),
        );
      };

      const foundWLCountry = (runtimeConfig.countriesWithWhiteLabels as any).find(
        (val) => val.id === IPLocationResponse.country?.id,
      );

      if (runtimeConfig.isMultiCountry && foundWLCountry && !runtimeConfig.isTelegramMiniApp) {
        setModalOpen(
          true,
          IPLocationResponse.country?.name || '',
          foundWLCountry.whiteLabel.domain,
          foundWLCountry.whiteLabel.defaultCountry.countryCode,
          'detected',
          true,
        );
        return;
      }

      if (
        runtimeConfig.isMultiCountry &&
        !foundWLCountry &&
        countryId !== runtimeConfig.defaultCountry.id &&
        !runtimeConfig.isTelegramMiniApp
      ) {
        setModalOpen(
          true,
          IPLocationResponse.country?.name || '',
          '',
          IPLocationResponse?.country?.countryCode || '',
          'detected',
          false,
          setFinalLocation,
        );
        return;
      }

      setFinalLocation();
    }
  }, [me, IPLocationResponse, meError, wlMode, isUnsubscribePage]);

  // Keep UI locale in sync with the language stored on the user profile.
  useEffect(() => {
    if (!me?.language || !pathname) {
      return;
    }

    if (me.language === locale) {
      return;
    }

    // The user's profile language may not be enabled on this whitelabel site
    // (e.g. account language is "ru" but this domain only serves "he"/"en").
    // Forcing a navigation to an unsupported locale gets stripped by the
    // proxy's foreign-locale redirect, which would otherwise bounce back here
    // and loop forever.
    if (!isAppLocale(me.language)) {
      return;
    }

    router.push(pathname, {
      locale: me.language,
    });

    const basePath = pathname === '/' ? '' : pathname;
    const localizedPath = me.language === routing.defaultLocale ? basePath || '/' : `/${me.language}${basePath}`;
    window.location.replace(`${localizedPath}${window.location.search}${window.location.hash}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, me?.language]);

  const parsedCoords = useMemo(() => JSON.parse(coords ?? ''), [coords]);

  const value = {
    wlMode: wlMode,
    regionId,
    cityId,
    countryId,
    districtId: '',
    updateLocation: updateFullLocationWithCheckingWL,
    coords: parsedCoords,
    currency: currency as TelegramCurrencyResponseCurrencyEnum,
    setCurrency,
    geoSearchString: geoSearchStr,
    updateLocationWithoutCheck: updateFullLocation,
  };

  return (
    <>
      <CookieStorageContext.Provider value={value}>{children}</CookieStorageContext.Provider>
    </>
  );
};

export const useRegionId = () => useContextSelector(CookieStorageContext, (value) => value.regionId);
export const useCityId = () => useContextSelector(CookieStorageContext, (value) => value.cityId);
export const useCountryId = () => useContextSelector(CookieStorageContext, (value) => value.countryId);
export const useDistrictId = () => useContextSelector(CookieStorageContext, (value) => value.districtId);
export const useSetLocation = () => useContextSelector(CookieStorageContext, (value) => value.updateLocation);
export const useSetLocationWithoutCheck = () =>
  useContextSelector(CookieStorageContext, (value) => value.updateLocationWithoutCheck);

export const useCoords = () => useContextSelector(CookieStorageContext, (value) => value.coords);
export const useSetCurrency = () => useContextSelector(CookieStorageContext, (value) => value.setCurrency);
export const useCurrency = () => useContextSelector(CookieStorageContext, (value) => value.currency);
export const useWLMode = () => useContextSelector(CookieStorageContext, (value) => value.wlMode);
export const useGeoSearchString = () => useContextSelector(CookieStorageContext, (value) => value.geoSearchString);
